import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Download, Package, ShoppingCart, Truck, RotateCcw, ClipboardList, Circle, Recycle, FileDown } from "lucide-react";
import { inventoryApi } from "./inventoryApi";
import { API } from "@/utils/constants";
import { formatCurrency, formatDateTime } from "./inventoryConstants";

const money = (v) => formatCurrency(v);

const fmtCell = (c, row) => {
  const v = row[c.key];
  if (c.format === "money") return money(v);
  if (c.format === "date") return formatDateTime(v);
  return v === null || v === undefined || v === "" ? "—" : String(v);
};

const toCsv = (columns, rows) => {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((r) => columns.map((c) => {
    let v = r[c.key];
    if (c.format === "money") v = (Number(v) || 0).toFixed(2);
    else if (c.format === "date") v = formatDateTime(v);
    if (v === null || v === undefined) v = "";
    return `"${String(v).replace(/"/g, '""')}"`;
  }).join(",")).join("\n");
  return `${header}\n${body}`;
};

const downloadCsv = (filename, columns, rows) => {
  const blob = new Blob([toCsv(columns, rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const ReportTable = ({ title, columns, rows, slug }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`report-table-${slug}`}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
      <h3 className="font-semibold text-slate-800 text-sm">{title} <span className="text-slate-400 font-normal">({rows.length})</span></h3>
      {rows.length > 0 && (
        <button onClick={() => downloadCsv(`${slug}.csv`, columns, rows)} className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid={`export-${slug}`}>
          <Download className="w-3.5 h-3.5" /> CSV
        </button>
      )}
    </div>
    {rows.length === 0 ? (
      <div className="px-4 py-6 text-center text-sm text-slate-400">No records</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            {columns.map((c) => <th key={c.key} className="px-4 py-2 font-medium whitespace-nowrap">{c.label}</th>)}
          </tr></thead>
          <tbody>
            {rows.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-0">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-2 text-slate-700 whitespace-nowrap">{fmtCell(c, r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 100 && <div className="px-4 py-2 text-xs text-slate-400">Showing first 100 of {rows.length} — export CSV for full list</div>}
      </div>
    )}
  </div>
);

const Tile = ({ label, value, accent }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 min-w-0">
    <div className="text-xs text-slate-500 font-medium truncate">{label}</div>
    <div className={`text-lg sm:text-xl font-bold mt-1 tabular-nums truncate ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);

const CATEGORIES = [
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "purchasing", label: "Purchasing", icon: ShoppingCart },
  { id: "units", label: "Units", icon: Truck },
  { id: "tires", label: "Tires", icon: Circle },
  { id: "salvage", label: "Salvage", icon: Recycle },
  { id: "cores", label: "Cores", icon: RotateCcw },
  { id: "requests", label: "Requests", icon: ClipboardList },
];

const CORE_COLS = [
  { key: "core_number", label: "Core #" }, { key: "vendor_name", label: "Vendor" },
  { key: "part_number", label: "Part #" }, { key: "unit_number", label: "Unit" },
  { key: "core_charge", label: "Charge", format: "money" }, { key: "status", label: "Status" },
];
const UNIT_COLS = [
  { key: "unit_number", label: "Unit" }, { key: "unit_type", label: "Type" },
  { key: "repairs", label: "Repairs" }, { key: "cost", label: "Parts Cost", format: "money" },
];

// Report definitions — drive both the on-screen tables and the PDF.
const REPORT_DEFS = {
  inventory: {
    title: "Inventory Report",
    api: "getInventoryReport",
    tiles: (d) => [
      { label: "Total Value", value: money(d.total_value), accent: "text-blue-600" },
      { label: "Actual", value: money(d.total_actual), accent: "text-green-600" },
      { label: "Estimated", value: money(d.total_estimated), accent: "text-amber-600" },
      { label: "Verified", value: money(d.total_verified), accent: "text-indigo-600" },
    ],
    tables: (d) => [
      { title: "Value by Category", slug: "inventory-by-category", rows: d.by_category, columns: [{ key: "category", label: "Category" }, { key: "count", label: "Parts" }, { key: "value", label: "Value", format: "money" }] },
      { title: "Value by Location", slug: "inventory-by-location", rows: d.by_location, columns: [{ key: "location", label: "Location" }, { key: "count", label: "Parts" }, { key: "value", label: "Value", format: "money" }] },
      { title: "Low Stock", slug: "low-stock", rows: d.low_stock, columns: [{ key: "part_number", label: "Part #" }, { key: "name", label: "Name" }, { key: "quantity_on_hand", label: "On Hand" }, { key: "reorder_point", label: "Reorder" }] },
      { title: "Parts with Estimated Value", slug: "estimated-value", rows: d.estimated_value_parts, columns: [{ key: "part_number", label: "Part #" }, { key: "name", label: "Name" }, { key: "value", label: "Value", format: "money" }] },
      { title: "Parts Missing Photos", slug: "missing-photos", rows: d.missing_photos, columns: [{ key: "part_number", label: "Part #" }, { key: "name", label: "Name" }, { key: "category", label: "Category" }] },
      { title: "Parts Missing Location", slug: "missing-location", rows: d.missing_location, columns: [{ key: "part_number", label: "Part #" }, { key: "name", label: "Name" }, { key: "category", label: "Category" }] },
    ],
  },
  purchasing: {
    title: "Purchasing Report",
    api: "getPurchasingReport",
    tiles: (d) => [
      { label: "Total Spend", value: money(d.total_spend), accent: "text-blue-600" },
      { label: "Open POs", value: d.open_purchase_orders.length, accent: "text-amber-600" },
    ],
    tables: (d) => [
      { title: "Vendor Spend", slug: "vendor-spend", rows: d.vendor_spend, columns: [{ key: "vendor", label: "Vendor" }, { key: "receipts", label: "Receipts" }, { key: "total", label: "Total Spend", format: "money" }] },
      { title: "Purchases by Month", slug: "purchases-by-month", rows: d.by_month, columns: [{ key: "month", label: "Month" }, { key: "total", label: "Total", format: "money" }] },
      { title: "Open Purchase Orders", slug: "open-pos", rows: d.open_purchase_orders, columns: [{ key: "po_number", label: "PO #" }, { key: "vendor_name", label: "Vendor" }, { key: "status", label: "Status" }, { key: "line_count", label: "Lines" }, { key: "subtotal", label: "Subtotal", format: "money" }] },
    ],
  },
  units: {
    title: "Unit Cost Report",
    api: "getUnitsReport",
    tiles: (d) => [
      { label: "Total Parts Cost (all units)", value: money(d.total_cost), accent: "text-blue-600" },
      { label: "Units with History", value: d.by_unit.length, accent: "text-slate-700" },
    ],
    tables: (d) => [
      { title: "Cost by Unit Type", slug: "cost-by-type", rows: d.by_type, columns: [{ key: "unit_type", label: "Unit Type" }, { key: "cost", label: "Parts Cost", format: "money" }] },
      { title: "Most Expensive Units", slug: "most-expensive-units", rows: d.most_expensive_units, columns: UNIT_COLS },
      { title: "Most Frequently Repaired Units", slug: "most-repaired-units", rows: d.most_repaired_units, columns: UNIT_COLS },
      { title: "Parts Cost by Unit (all)", slug: "cost-by-unit", rows: d.by_unit, columns: UNIT_COLS },
    ],
  },
  tires: {
    title: "Tire Report",
    api: "getTiresReport",
    tiles: (d) => [
      { label: "In-Stock Value", value: money(d.total_stock_value), accent: "text-blue-600" },
      { label: "Installed Value", value: money(d.total_installed_value), accent: "text-green-600" },
      { label: "Total Tire Spend", value: money(d.total_tire_spend), accent: "text-indigo-600" },
      { label: "Tires In Stock", value: d.total_stock_qty, accent: "text-slate-700" },
    ],
    tables: (d) => [
      { title: "Tire Inventory (In Stock)", slug: "tire-inventory", rows: d.in_stock, columns: [{ key: "tire_brand", label: "Brand" }, { key: "tire_size", label: "Size" }, { key: "quantity", label: "Qty" }, { key: "cost", label: "Cost/ea", format: "money" }, { key: "total_value", label: "Value", format: "money" }, { key: "condition", label: "Condition" }, { key: "dot_date", label: "DOT" }, { key: "location", label: "Location" }] },
      { title: "Tires Installed on Units", slug: "tires-installed", rows: d.installed, columns: [{ key: "unit_number", label: "Unit" }, { key: "tire", label: "Tire" }, { key: "wheel_position", label: "Position" }, { key: "quantity", label: "Qty" }, { key: "cost", label: "Cost", format: "money" }, { key: "date_installed", label: "Installed", format: "date" }, { key: "installed_by", label: "By" }] },
      { title: "Tire Cost by Unit", slug: "tire-cost-by-unit", rows: d.by_unit, columns: [{ key: "unit_number", label: "Unit" }, { key: "qty", label: "Tires" }, { key: "cost", label: "Cost", format: "money" }] },
      { title: "Tires by Size", slug: "tires-by-size", rows: d.by_size, columns: [{ key: "tire_size", label: "Size" }, { key: "qty", label: "Qty in Stock" }, { key: "value", label: "Value", format: "money" }] },
      { title: "Tires by Brand", slug: "tires-by-brand", rows: d.by_brand, columns: [{ key: "tire_brand", label: "Brand" }, { key: "qty", label: "Qty in Stock" }, { key: "value", label: "Value", format: "money" }] },
    ],
  },
  salvage: {
    title: "Salvage Report",
    api: "getSalvageReport",
    tiles: (d) => [
      { label: "Total Salvage Value", value: money(d.total_value), accent: "text-purple-600" },
      { label: "Items", value: d.count, accent: "text-slate-700" },
      { label: "In Inventory", value: d.in_inventory_count, accent: "text-green-600" },
    ],
    tables: (d) => [
      { title: "Salvaged Parts", slug: "salvaged-parts", rows: d.items, columns: [{ key: "description", label: "Description" }, { key: "part_number", label: "Part #" }, { key: "source_unit_number", label: "Source Unit" }, { key: "condition", label: "Condition" }, { key: "quantity", label: "Qty" }, { key: "value", label: "Value", format: "money" }, { key: "value_type", label: "Value Type" }, { key: "in_inventory", label: "In Inv." }, { key: "location", label: "Location" }] },
      { title: "By Value Type", slug: "salvage-by-value-type", rows: d.by_value_type, columns: [{ key: "value_type", label: "Value Type" }, { key: "count", label: "Items" }, { key: "value", label: "Value", format: "money" }] },
    ],
  },
  cores: {
    title: "Core Report",
    api: "getCoresReport",
    tiles: (d) => [
      { label: "Money At Risk", value: money(d.money_at_risk), accent: "text-red-600" },
      { label: "Completed Credits", value: money(d.completed_amount), accent: "text-green-600" },
      { label: "Lost Charges", value: money(d.lost_amount), accent: "text-red-700" },
    ],
    tables: (d) => [
      { title: "Open Cores", slug: "open-cores", rows: d.open_cores, columns: CORE_COLS },
      { title: "Returned Awaiting Credit", slug: "awaiting-credit", rows: d.awaiting_credit, columns: CORE_COLS },
      { title: "Completed Credits", slug: "completed-credits", rows: d.completed_credits, columns: CORE_COLS },
      { title: "Lost Core Charges", slug: "lost-cores", rows: d.lost_cores, columns: CORE_COLS },
    ],
  },
  requests: {
    title: "Parts Request Report",
    api: "getRequestsReport",
    tiles: () => [],
    tables: (d) => [
      { title: "Open Requests", slug: "open-requests", rows: d.open_requests, columns: [{ key: "request_number", label: "Request #" }, { key: "priority", label: "Priority" }, { key: "status", label: "Status" }, { key: "unit_number", label: "Unit" }, { key: "requested_by", label: "By" }, { key: "line_count", label: "Lines" }] },
      { title: "Truck Down Requests", slug: "truck-down-requests", rows: d.truck_down_requests, columns: [{ key: "request_number", label: "Request #" }, { key: "status", label: "Status" }, { key: "unit_number", label: "Unit" }, { key: "requested_by", label: "By" }] },
      { title: "Requests Requiring Purchase", slug: "requiring-purchase", rows: d.requiring_purchase, columns: [{ key: "request_number", label: "Request #" }, { key: "unit_number", label: "Unit" }, { key: "requested_by", label: "By" }] },
      { title: "Requests by Unit", slug: "requests-by-unit", rows: d.by_unit, columns: [{ key: "unit_number", label: "Unit" }, { key: "count", label: "Requests" }] },
      { title: "Requests by Employee", slug: "requests-by-employee", rows: d.by_employee, columns: [{ key: "employee", label: "Employee" }, { key: "count", label: "Requests" }] },
    ],
  },
};

export const ReportsSection = () => {
  const [cat, setCat] = useState("inventory");
  const [data, setData] = useState(null);
  const [loadedCat, setLoadedCat] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true); setData(null); setLoadedCat(null);
    try {
      const def = REPORT_DEFS[cat];
      setData(await inventoryApi[def.api]());
      setLoadedCat(cat);
    } catch { toast.error("Failed to load report"); }
    setLoading(false);
  }, [cat]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const ready = !loading && data && loadedCat === cat;
  const def = REPORT_DEFS[cat];

  const tiles = ready ? (def.tiles(data) || []) : [];
  const tables = ready ? (def.tables(data) || []) : [];

  return (
    <div className="space-y-4" data-testid="reports-section">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`report-cat-${c.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${cat === c.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                <Icon className="w-4 h-4 shrink-0" /> {c.label}
              </button>
            );
          })}
        </div>
        <a
          href={`${API}/inventory/reports/${cat}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="parts-report-pdf"
          className="shrink-0 inline-flex items-center gap-1 h-10 px-4 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <FileDown className="w-4 h-4" /> Download PDF
        </a>
      </div>

      {!ready ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-3">
          {tiles.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {tiles.map((t, i) => <Tile key={i} label={t.label} value={t.value} accent={t.accent} />)}
            </div>
          )}
          {tables.map((tbl) => (
            <ReportTable key={tbl.slug} title={tbl.title} slug={tbl.slug} rows={tbl.rows} columns={tbl.columns} />
          ))}
        </div>
      )}
    </div>
  );
};
